import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {catchError, exhaustMap, filter, map, switchMap} from "rxjs/operators";
import {Observable, of} from "rxjs";
import {OrderActions, OrderStatusChangedResponse} from "../store/actions/order.actions";
import {HttpResponse} from "@angular/common/http";
import {BillingInformation, Order, TermsAndConditions, UTMParams} from "../store/state/order.state";
import {LoginActions} from "../store/actions/login.actions";
import {EndpointsProvider} from "../umbrella-common/endpoints/endpoint-provider.service";
import {AdditionalQuestionsRequest} from "../umbrella-common/endpoints/request-bodies";

@Injectable()
export class OrderEffects {

	constructor(private actions$: Actions, private endpoints: EndpointsProvider) {
	}

	requestOffer$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.REQUEST_ORDER),
		exhaustMap(data => this.requestOrder(data.order, data.utmParams)
			.pipe(
				map(response => {
					return response.body.accountAlreadyExist ?
						OrderActions.ORDER_REQUEST_ACCOUNT_EXISTS({orderRequestResponse: response.body}) :
						OrderActions.ORDER_REQUEST_SUCCESSFUL({orderRequestResponse: response});
				}),
				catchError(error => {
					return of(OrderActions.ORDER_REQUEST_FAILED({error}));
				})
			)
		)
	));

	requestEstimate$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.REQUEST_ESTIMATE),
		exhaustMap(data => this.requestEstimate(data.order, data.correlationId)
			.pipe(
				map(response => {
					return OrderActions.REQUEST_ESTIMATE_SUCCESSFUL({response});
				}),
				catchError(error => {
					return of(OrderActions.REQUEST_ESTIMATE_FAILED({error}));
				})
			)
		)
	));

	sendAdditionalQuestions$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.SEND_ADDITIONAL_QUESTIONS),
		exhaustMap(data => this.sendAdditionalQuestions(data.correlationId, data.requests)
			.pipe(
				map(response => {
					return OrderActions.SEND_ADDITIONAL_QUESTIONS_SUCCESSFUL({sendResponse: response});
				}),
				catchError(error => {
					return of(OrderActions.SEND_ADDITIONAL_QUESTIONS_FAILED({error}));
				})
			)
		)
	));

	onOfferUpdatedAfterEstimateRequest$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.REQUEST_ESTIMATE_SUCCESSFUL),
		map(data => OrderActions.GO_TO_ORDER_ADDITIONAL_QUESTIONS({correlationId: data.response.body.correlationId}))
	));

	onOfferUpdatedAfterRequest$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.ORDER_REQUEST_SUCCESSFUL),
		filter(action => !action.orderRequestResponse.body.accountAlreadyExist),
		map(data => OrderActions.GO_TO_ORDER_ESTIMATE_PAGE({correlationId: data.orderRequestResponse.body.correlationId}))
	));

	onAdditionalQuestionsSubmitted$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.SEND_ADDITIONAL_QUESTIONS_SUCCESSFUL),
		map(data => OrderActions.GO_TO_ORDER_CONFIRMATION({correlationId: data.sendResponse.body.correlationId}))
	));

	clearStoreAfterLoggingOut$ = createEffect(() => this.actions$.pipe(
		ofType(LoginActions.LOG_OUT, LoginActions.LOG_IN_ATTEMPT),
		map(() => OrderActions.CLEAR_ORDER())
	));

	confirmOrder$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.CONFIRM_ORDER),
		exhaustMap(action => this.confirmOrder(action.correlationId, action.termsAndConditions).pipe(
			map(order => OrderActions.CONFIRM_ORDER_SUCCESSFUL({order})),
			catchError(error => of(OrderActions.CONFIRM_ORDER_FAILURE({error})))
		))
	));

	sendOrderToReview$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.CONFIRM_AND_SEND_TO_REVIEW),
		exhaustMap(action => this.sendOrderToReview(action.correlationId, action.termsAndConditions).pipe(
			map(order => OrderActions.CONFIRM_AND_SEND_TO_REVIEW_SUCCESSFUL({order})),
			catchError(error => of(OrderActions.CONFIRM_AND_SEND_TO_REVIEW_FAILURE({error})))
		))
	));

	goToUploadDocuments$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.CONFIRM_ORDER_SUCCESSFUL),
		map(data => OrderActions.GO_TO_UPLOAD_TRANSFER_DOCUMENTS({correlationId: data.order.orderCorrelationId}))
	));

	goToUnderReviewPage$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.CONFIRM_AND_SEND_TO_REVIEW_SUCCESSFUL, OrderActions.UPLOAD_DOCUMENTS_SUCCESSFUL),
		map(action => OrderActions.GO_TO_ORDER_UNDER_REVIEW_PAGE({correlationId: action.order.orderCorrelationId}))
	));

	goToPropoerStepAfterProposalDecision$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.ACCEPT_REVIEW_CHANGES_SUCCESSFUL, OrderActions.DECLINE_REVIEW_CHANGES_SUCCESSFUL),
		map(action => OrderActions.GO_TO_ORDER_CURRENT_STEP({forceRefresh: false}))
	));

	onUploadDocuments$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.UPLOAD_DOCUMENTS),
		exhaustMap(data => this.uploadDocuments(data.correlationId, data.certificateDocuments, data.reportDocuments)
			.pipe(
				map(order => OrderActions.UPLOAD_DOCUMENTS_SUCCESSFUL({order})),
				catchError(error => of(OrderActions.UPLOAD_DOCUMENTS_FAILURE({error})))
			)
		)
	));

	goToOrderDeclinedPage$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.DECLINE_ORDER_BY_CLIENT_SUCCESSFUL),
		filter(action => action.navigateToDeclinePage),
		map(action => OrderActions.GO_TO_ORDER_DECLINED_PAGE({correlationId: action.statusChange.correlationId})))
	);

	onOrderDeclinedByClient$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.DECLINE_ORDER_BY_CLIENT),
		exhaustMap(action => this.declineByClient(action.correlationId, action.reason).pipe(
			map(statusChange => OrderActions.DECLINE_ORDER_BY_CLIENT_SUCCESSFUL({statusChange, navigateToDeclinePage: action.navigateToDeclinePage})),
			catchError(error => of(OrderActions.DECLINE_ORDER_BY_CLIENT_FAILURE({error})))
		))
	));

	getNewEstimateAfterClientDecline$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.GET_NEW_ESTIMATE_AFTER_DECLINE),
		map(action => OrderActions.GO_TO_GET_AN_ESTIMATE_PAGE({}))
	));

	logoutAnonymousClientAfterDecline$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.GET_NEW_ESTIMATE_AFTER_DECLINE),
		filter(action => action.isAnonymousClient),
		map(action => LoginActions.LOG_OUT())
	));

	acceptReviewChanges$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.ACCEPT_REVIEW_CHANGES),
		exhaustMap(data => this.acceptReviewChanges(data.correlationId).pipe(
			map(statusChangeResponse => OrderActions.ACCEPT_REVIEW_CHANGES_SUCCESSFUL({correlationId: statusChangeResponse.correlationId, statusChange: statusChangeResponse})),
			catchError(error => of(OrderActions.ACCEPT_REVIEW_CHANGES_FAILED({error})))
		))
	));

	declineReviewChnages$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.DECLINE_REVIEW_CHANGES),
		exhaustMap(data => this.declineReviewChanges(data.correlationId, data.reason).pipe(
			map(statusChangeResponse => OrderActions.DECLINE_REVIEW_CHANGES_SUCCESSFUL({correlationId: statusChangeResponse.correlationId, statusChange: statusChangeResponse})),
			catchError(error => of(OrderActions.DECLINE_REVIEW_CHANGES_FAILED({error})))
		))
	));

	onChangeBillingInformation$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.CHANGE_BILLING_INFORMATION),
		exhaustMap(action => this.changeBillingInformation(action.correlationId, action.billingInformationsRequest).pipe(
			map(changed => OrderActions.CHANGE_BILLING_INFORMATION_SUCCESS({order: changed})),
			catchError(error => of(OrderActions.CHANGE_BILLING_INFORMATION_FAILURE()))
		))
	));

	onSetBillingInformations$ = createEffect(() => this.actions$.pipe(
		ofType(OrderActions.SET_BILLING_INFORMATIONS),
		switchMap(action => {
			const billingInformations = action.billingInformations;
			const person = billingInformations.billingContactPerson;
			const company = billingInformations.billingCompany;

			let personActions = [];
			if (person) {
				personActions = [
					OrderActions.SET_BILLING_CONTACT_PERSON_FIRST_NAME({firstName: person.firstName}),
					OrderActions.SET_BILLING_CONTACT_PERSON_LAST_NAME({lastName: person.lastName}),
					OrderActions.SET_BILLING_CONTACT_PERSON_JOB_TITLE({jobTitle: person.jobTitle}),
					OrderActions.SET_BILLING_COUNTRY_CODE({countryCode: person.countryCode}),
					OrderActions.SET_BILLING_PHONE_NUMBER({phoneNumber: person.phoneNumber}),
					OrderActions.SET_BILLING_CLIENT_EMAIL({email: person.email})
				];
			}

			let companyActions = [];
			if (company) {
				companyActions = [
					OrderActions.SET_BILLING_COMPANY_NAME({companyName: company.name}),
					OrderActions.SET_BILLING_CITY({city: company.city}),
					OrderActions.SET_BILLING_STREET({street: company.street}),
					OrderActions.SET_BILLING_ZIP_CODE({zipCode: company.zipCode}),
					OrderActions.SET_BILLING_STATE({state: company.state}),
					OrderActions.SET_BILLING_COUNTRY({countryName: company.country})
				];
			}

			return [
				OrderActions.TOGGLE_PURCHASE_PAYMENT({isPurchaseNumberRequiredForPayment: billingInformations.isPurchaseNumberRequiredForPayment}),
				OrderActions.TOGGLE_SAME_COMPANY({sameCompanyAndContactPersonAsProvided: billingInformations.sameCompanyAndContactPersonAsProvided}),
				OrderActions.SET_PURCHASE_NUMBER({purchaseOrderNumber: billingInformations.purchaseOrderNumber}),
				OrderActions.TOGGLE_TRAVEL_EXPENSE({coverTravelExpenses: billingInformations.coverTravelExpenses}),
				OrderActions.SET_ADDITIONAL_BILLING_INFORMATION({additionalInformation: billingInformations.additionalInformation}),
				...personActions,
				...companyActions
			];
		})
	));

	private changeBillingInformation(correlationId: string, billingInformationsRequest: BillingInformation): Observable<Order> {
		return this.endpoints.client.changeBillingInformation(correlationId, billingInformationsRequest);
	}

	private requestOrder(order: Order, utmParams: UTMParams): Observable<HttpResponse<any>> {
		return this.endpoints.client.requestOrder(order, utmParams);
	}

	private requestEstimate(order: Order, correlationId: string): Observable<HttpResponse<any>> {
		return this.endpoints.client.requestEstimate(correlationId, order);
	}

	private sendAdditionalQuestions(correlationId: string, requests: AdditionalQuestionsRequest[]): Observable<HttpResponse<any>> {
		return this.endpoints.client.submitAdditionalQuestions(correlationId, requests);
	}

	private confirmOrder(correlationId: string, termsAndConditions: TermsAndConditions): Observable<Order> {
		return this.endpoints.client.confirmOrder(correlationId, termsAndConditions);
	}

	private sendOrderToReview(correlationId: string, termsAndConditions: TermsAndConditions): Observable<Order> {
		return this.endpoints.client.sendOrderToReview(correlationId, termsAndConditions);
	}

	private uploadDocuments(correlationId: string, certificateDocuments: File[], reportDocuments: File[]): Observable<Order> {
		return this.endpoints.client.uploadTransferDocuments(correlationId, certificateDocuments, reportDocuments);
	}

	private declineByClient(correlationId: string, reason: string): Observable<OrderStatusChangedResponse> {
		return this.endpoints.client.declineOrder(correlationId, reason);
	}

	private acceptReviewChanges(correlationId: string): Observable<OrderStatusChangedResponse> {
		return this.endpoints.client.acceptReviewerChanges(correlationId);
	}

	private declineReviewChanges(correlationId: string, reason: string): Observable<OrderStatusChangedResponse> {
		return this.endpoints.client.declineReviewerChanges(correlationId, reason);
	}
}
